<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  authType: {
    type: String,
    default: 'none',
  },
  updatedAt: {
    type: Number,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => [
  {
    label: t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.EDIT_TOOL'),
    value: 'edit',
    action: 'edit',
    icon: 'i-lucide-pencil-line',
  },
  {
    label: t('CAPTAIN.CUSTOM_TOOLS.OPTIONS.DELETE_TOOL'),
    value: 'delete',
    action: 'delete',
    icon: 'i-lucide-trash',
  },
]);

const timestamp = computed(() =>
  dynamicTime(props.updatedAt || props.createdAt)
);

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};

const authTypeLabel = computed(() => {
  return t(
    `CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.${props.authType.toUpperCase()}`
  );
});
</script>

<template>
  <div class="relative">
    <CardLayout>
      <div class="flex relative justify-between w-full gap-2 min-w-0">
        <span class="text-base text-n-slate-12 line-clamp-1 font-medium min-w-0 flex-1">
          {{ title }}
        </span>
        <div class="flex items-center gap-2 shrink-0">
          <Policy
            v-on-clickaway="() => toggleDropdown(false)"
            :permissions="['administrator']"
            class="relative flex items-center group"
          >
            <Button
              icon="i-lucide-ellipsis-vertical"
              color="slate"
              size="xs"
              class="rounded-md group-hover:bg-n-alpha-2"
              @click="toggleDropdown()"
            />
            <DropdownMenu
              v-if="showActionsDropdown"
              :menu-items="menuItems"
              class="mt-1 ltr:right-0 rtl:right-0 top-full z-50"
              @action="handleAction($event)"
            />
          </Policy>
        </div>
      </div>
      <div class="flex items-center justify-between w-full gap-4 min-w-0">
        <div class="flex items-center gap-3 flex-1 min-w-0">
          <span
            v-if="description"
            class="text-sm truncate text-n-slate-11 min-w-0 flex-1"
          >
            {{ description }}
          </span>
          <span
            v-if="authType !== 'none'"
            class="text-sm shrink-0 text-n-slate-11 inline-flex items-center gap-1 whitespace-nowrap"
          >
            <i class="i-lucide-lock text-base" />
            {{ authTypeLabel }}
          </span>
        </div>
        <span class="text-sm text-n-slate-11 shrink-0 whitespace-nowrap">
          {{ timestamp }}
        </span>
      </div>
    </CardLayout>
  </div>
</template>
